import React, { useEffect, useState, useRef } from 'react'
import { FormattedMessage, FormattedDate, FormattedTime, useIntl } from 'react-intl'
import { Transition } from '@headlessui/react'
import { Link, NavLink, useLocation } from 'react-router-dom'
import Icon from 'components/Icon'
import { withRouter } from 'react-router'
import useCurrentUser from 'hooks/useCurrentUser'
import NavItem from './components/NavItem'
import messages from './messages'
import Modal from 'components/Modal'

const Views = {
  HIDDEN: 'HIDDEN',
  COLLAPSED: 'COLLAPSED',
  FULL: 'FULL',
}

const SidebarLayout = ({
  CommandPalette,
  brand,
  children,
  routes,
  footerRoutes = [],
  notifications,
  onReload,
  version = null,
  lastUpdate = null,
  apps = [],
  ...props
}) => {
  const { formatMessage } = useIntl()
  const location = useLocation()
  const [showNav, setShowNav] = useState(false)
  const [view, setView] = useState(Views.FULL)
  const [showUserMenu, setShowUserMenu] = useState(false)
  const [showAppsMenu, setShowAppsMenu] = useState(false)
  const [showShortcutsModal, setShowShortcutModal] = useState(false)
  const [showNotifications, setShowNotifications] = useState(false)
  const { image, name, username, track } = useCurrentUser()
  const shortcutRef = useRef()

  useEffect(() => {
    if (window.localStorage) {
      setView(localStorage.getItem('voiceportal.nav.view') || Views.FULL)
    }
    
    const up = window.addEventListener('keyup', () => {
      setShowShortcutModal(false)
      clearTimeout(shortcutRef.current)
    })

    const down = window.addEventListener('keydown', (e) => {
      if (!['INPUT', 'TEXTAREA'].includes(e.target.tagName) && 'shift' === e.key.toLowerCase()) {
        shortcutRef.current = setTimeout(() => setShowShortcutModal(true), 400)
      }
    })

    return () => {
      window.removeEventListener('keydown', down)
      window.removeEventListener('keydown', up)
    }
  }, []);

  const shuffleView = () => {
    const nextView = {
      [Views.FULL]: Views.COLLAPSED,
      [Views.COLLAPSED]: Views.HIDDEN,
      [Views.HIDDEN]: Views.FULL,
    }[view]

    if (window.localStorage) {
      localStorage.setItem('voiceportal.nav.view', nextView)
    }

    setView(nextView)

    track.increment('navigation.cta', { action: 'shuffleView', type: nextView })
  }

  const renderBody = (body) => {
    const hasHtmlTags = /<\/?[a-z][\s\S]*>/i.test(body);
    if (hasHtmlTags) {
      return <div className="mt-1 text-sm text-gray-500 truncate" dangerouslySetInnerHTML={{ __html: body }} />;
    } else {
      return <p className="mt-1 text-sm text-gray-500 truncate">{body ? body.slice(0, 120) : '-'}</p>;
    }
  };

  return (
    <div className="h-screen flex overflow-hidden bg-gray-50">
      <Modal show={showShortcutsModal} onClose={() => setShowShortcutModal(false)}>
        <h1 className="text-md font-medium text-gray-600 mb-4">
          Keyboard Shortcuts
        </h1>
        <div className="grid grid-cols-2">
          <kbd>Shift + /</kbd>
          <span>Open global search</span>
          <kbd>Shift + Enter</kbd>
          <span>Reload or expand window</span>
          { routes
              .filter(({ kbd, routes = [] }) => routes.some(({ kbd }) => kbd) || kbd)
              .map(({ routes = [], kbd, name, icon }) => (
            <React.Fragment key={`fragment-${name}`}>
              { kbd &&
                <>
                  <kbd>{ kbd.join('+') }</kbd>
                  <span>{ name }</span>
                </>
              }

              { routes.filter(({ kbd }) => kbd).map(({ kbd, name: subName, icon }) => (
                <React.Fragment key={`fragment-${subName}`}>
                  <kbd>{ kbd.join('+') }</kbd>
                  <span>{ name } &rarr; { subName }</span>
                </React.Fragment>
              )) }
            </React.Fragment>
          )) }
        </div>
      </Modal>

      <Transition show={showNav} className="md:hidden">
        <div className="fixed inset-0 flex z-40 transition-opacity ease-linear duration-300 opacity-100">

          <Transition.Child
            enter="transition-opacity ease-linear duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="transition-opacity ease-linear duration-300"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
            className="fixed inset-0"
          >
            <div className="absolute inset-0 bg-gray-600 opacity-75" />
          </Transition.Child>

          <Transition.Child
            enter="transition ease-in-out duration-300 transform"
            enterFrom="-translate-x-full"
            enterTo="translate-x-0"
            leave="transition ease-in-out duration-300 transform"
            leaveFrom="translate-x-0"
            leaveTo="-translate-x-full"
            className="relative flex-1 flex flex-col max-w-xs w-full bg-gray-50"
          >
            <div className="absolute top-0 right-0 -mr-12 pt-2">
              <button
                onClick={() => {
                  setShowNav(!showNav)
                  track.increment('navigation.cta', { action: 'toggleVisibility' })
                }}
                className="ml-1 flex items-center justify-center h-10 w-10 rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
                title={formatMessage(messages.closeSidebar)}
              >
                <span className="sr-only">
                  <FormattedMessage {...messages.closeSidebar } />
                </span>
                <Icon icon="times" />
              </button>
            </div>
            <Link
              to="/dashboard"
              className="shrink-0 flex items-center py-2 px-4 bg-primary"
              onClick={() => track.increment('navigation.cta', { action: 'logo' })}
            >
              <img className="h-6 w-auto" src={brand.logo} alt={brand.name} />
            </Link>
            <div className="mt-5 flex-1 h-0 overflow-y-auto">
              <nav className="px-2 space-y-1">
                { routes.map((route, i) => (
                  <NavItem key={`${route.path}-${i}-mobile`} route={route} />
                )) }
              </nav>
            </div>
          </Transition.Child>

          <div className="shrink-0 w-14" aria-hidden="true" />
        </div>
      </Transition>

      <div className="hidden bg-gray-50 md:flex md:shrink-0 shadow-md">
        <div className={{ [Views.FULL]: 'flex flex-col w-64', [Views.COLLAPSED]: 'flex flex-col w-14', [Views.HIDDEN]: 'hidden'}[view]}>
          <div className="flex flex-col h-0 flex-1">
            <div className="flex-1 flex flex-col pb-4 overflow-y-auto">
              <Link
                to="/dashboard"
                className="flex items-center shrink-0 h-12 xl:h-14 px-4 bg-primary"
                onClick={() => track.increment('navigation.cta', { action: 'logo' })}
              >
                <img className="h-6 w-auto" src={brand.logo} alt={brand.name} />
              </Link>
              <nav className="mt-2 flex-1 px-2 space-y-2">
                { routes.map((route, i) => (
                  <NavItem key={`${route.path}-${i}-desktop`} route={route} showLabel={view != Views.COLLAPSED} />
                )) }
              </nav>
            </div>
            { !!footerRoutes.length &&
              <div className="shrink-0 border-t border-gray-200 p-4 space-y-2">
                <button
                  className="block text-xs font-medium text-gray-600 hover:text-primary"
                  onClick={() => {
                    setShowShortcutModal(true)
                    track.increment('navigation.cta', { action: 'showShortcutModal' })
                  }}
                >
                  <Icon className="font-bold mr-1 fa-fw" icon={['fal', 'keyboard']} />
                  { view != Views.COLLAPSED &&
                    <span className="uppercase">Show keyboard shortcuts</span>
                  }
                </button>
                { footerRoutes.map(({ name, path, icon }) => (
                  <Link
                    key={path}
                    to={path}
                    className="block text-xs font-medium text-gray-600 hover:text-primary"
                    onClick={() => track.increment('navigation', { path })}
                  >
                    <Icon className="font-bold mr-1 fa-fw" icon={['fal', icon]} />
                    { view != Views.COLLAPSED &&
                      <span className="uppercase">{ name }</span>
                    }
                  </Link>
                )) }

                { version && view == Views.FULL &&
                  <div className="text-xs text-gray-500 pt-2">
                      <FormattedMessage {...messages.appVersion} values={{
                        version: <span className="font-mono">{ version.substring(0, 9) }</span>,
                        date: <FormattedDate value={new Date(lastUpdate * 1000)} />,
                        time: <FormattedTime value={new Date(lastUpdate * 1000)} />,
                      }} tagName="div" />
                  </div>
                }
              </div>
            }
            { brand && brand.displayName &&
              <div className="shrink-0 flex border-t border-gray-200 p-4">
                <Link to="/company/settings" className="shrink-0 w-full group block">
                  <div className="flex items-center">
                    <div>
                      <img className="inline-block h-8 w-8 rounded-full" src={brand.symbol} alt={brand.name} />
                    </div>
                    <div className="ml-3">
                      { view != Views.COLLAPSED &&
                        <>
                          <p className="text-sm font-medium text-gray-900">
                            { brand.displayName }
                          </p>
                          <p className="text-xs font-medium text-gray-700 group-hover:text-primary">
                            <FormattedMessage {...messages.companyDetails}/>
                          </p>
                        </>
                      }
                    </div>
                  </div>
                </Link>
              </div>
            }
          </div>
        </div>
      </div>
      <div className="flex flex-col w-0 flex-1 overflow-hidden bg-white shadow">
        <div className="relative z-10 shrink-0 flex h-12 xl:h-14 bg-primary">
          <button
            className="md:hidden px-4 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-darker text-primary-darker hover:text-white"
            title={formatMessage(messages.openSidebar)}
            onClick={() => {
              setShowNav(!showNav)
              track.increment('navigation.cta', { action: 'toggleVisibility' })
            }}
          >
            <span className="sr-only"><FormattedMessage{...messages.openSidebar}/></span>
            <Icon icon="bars" />
          </button>

          <button
            onClick={shuffleView}
            className="hidden md:block px-4 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-darker text-primary-darker hover:text-white"
            title={formatMessage(messages.openSidebar)}
          >
            <span className="sr-only"><FormattedMessage{...messages.openSidebar}/></span>
            <Icon icon="bars" />
          </button>

          <div className="flex-1 px-2 flex justify-between h-12 xl:h-14">
            <div className="hidden md:flex md:w-1/4 items-center">
              <ol className="font-medium capitalize flex items-center space-x-4">
                { location.pathname.slice(1).split('/').map((chunk, i, arr) => (
                  <Link to={`/${arr.slice(0, i + 1).join('/')}`} key={chunk} className="flex items-center group">
                      <Icon icon="chevron-right" className="text-xs text-primary-darker" />
                      <span className="ml-4 text-primary-darker group-hover:text-white">{ chunk }</span>
                  </Link>
                )) }
              </ol>
            </div>

            { CommandPalette &&
              <div className="w-3/4 md:w-1/2 flex items-center justify-center py-1 md:py-2">
                <CommandPalette inline />
              </div>
            }

            <div className="w-1/4 ml-4 flex items-center justify-end md:ml-6">
              { onReload &&
                <button
                  onClick={(event) => {
                    onReload(event)
                    track.increment('navigation.cta', { action: 'reload' })
                  }}
                  className="hidden md:flex max-w-xs w-8 h-8 bg-primary justify-center items-center text-sm rounded-full focus:outline-none focus:ring-2 focus:ring-primary-darker text-white z-10"
                  id="reload-button"
                  aria-haspopup="true"
                >
                  <span className="sr-only">Reload</span>
                  <Icon icon="expand" className="text-xl" />
                </button>
              }

              { !!apps.length &&
                <div className="ml-3 relative">
                  <div>
                    <button
                      onBlur={() => setTimeout(() => setShowAppsMenu(false), 200)}
                      onClick={() => {
                        setShowAppsMenu(!showAppsMenu)
                        track.increment('navigation.cta', { action: 'toggleAppsMenu' })
                      }}
                      className="hidden md:flex max-w-xs w-8 h-8 bg-primary justify-center items-center text-sm rounded-full focus:outline-none focus:ring-2 focus:ring-primary-darker text-white z-10"
                      id="apps-menu"
                      aria-haspopup="true"
                    >
                      <span className="sr-only"><FormattedMessage{...messages.openAppsMenu}/></span>
                      <Icon icon="th" className="text-xl" />
                    </button>
                  </div>

                  <Transition
                    show={showAppsMenu}
                    enter="transition ease-out duration-100"
                    enterFrom="transform opacity-0 scale-95"
                    enterTo="transform opacity-100 scale-100"
                    leave="transition ease-in duration-75"
                    leaveFrom="transform opacity-100 scale-100"
                    leaveTo="transform opacity-0 scale-95"
                    className="origin-top-right absolute right-0 mt-2 w-96 rounded-sm shadow-md bg-white ring-1 ring-black ring-opacity-5 p-4 grid grid-cols-3 gap-4"
                    role="menu"
                    aria-orientation="vertical"
                    aria-labelledby="apps-menu"
                  >
                    { apps.map(([title, href, icon, beta]) => (
                      <a key={href} href={href} target="_blank" className="p-2 text-center rounded hover:bg-gray-100">
                        <div>
                          <Icon icon={icon} className="text-2xl text-primary" />
                        </div>
                        <div className="font-medium text-sm">
                          { title }
                        </div>
                        { beta &&
                          <kbd className="bg-blue-100 text-blue-600 font-medium px-2 py-1 text-xs">Beta</kbd>
                        }
                      </a>
                    )) }

                  </Transition>
                </div>
              }

              <div className="ml-3 relative">
                <div>
                  <span className="inline-block relative">
                    <button
                      onClick={() => {
                        setShowNotifications(!showNotifications)
                        track.increment('navigation.cta', { action: 'toggleNotifications' })
                      }}
                      onBlur={() => setTimeout(() => setShowNotifications(false), 200)}
                      className="max-w-xs w-8 h-8 bg-primary flex justify-center items-center text-sm rounded-full focus:outline-none focus:ring-2 focus:ring-primary-darker text-white z-10"
                      id="notifications"
                      aria-haspopup="true"
                    >
                      <span className="sr-only"><FormattedMessage{...messages.openNotifications}/></span>
                      <Icon className="text-xl" icon="bell" />
                    </button>

                    { !!notifications.length &&
                      <span className="absolute -top-0.5 -left-0.5 block h-2 w-2">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white"></span>
                        <span className="absolute inline-flex rounded-full h-2 w-2 bg-red-600"></span>
                      </span>
                    }
                  </span>
                </div>

                <Transition
                  show={showNotifications}
                  enter="transition ease-out duration-100"
                  enterFrom="transform opacity-0 scale-95"
                  enterTo="transform opacity-100 scale-100"
                  leave="transition ease-in duration-75"
                  leaveFrom="transform opacity-100 scale-100"
                  leaveTo="transform opacity-0 scale-95"
                  className="origin-top-right absolute right-0 -mr-12 md:mr-0 mt-2 w-screen px-2 md:px-0 md:w-96 rounded-sm shadow-md bg-white ring-1 ring-black ring-opacity-5 divide-y divide-gray-100 max-h-80 overflow-scroll"
                  role="notifications"
                  aria-orientation="vertical"
                  aria-labelledby="notifications"
                >
                  <ul className="divide-y divide-gray-200">
                    { !notifications.length ? (
                      <li className="pointer-events-auto flex">
                        <div className="w-0 flex-1 flex items-center p-4">
                          <FormattedMessage {...messages.notificationsEmptyState } />
                          <Icon icon="check" className="ml-2" />
                        </div>
                      </li>
                    ) : notifications.map(({ id, title, body, markSeen, created_at, url }) => (
                      <li className="pointer-events-auto flex" key={id}>
                        <div className="w-0 flex-1 flex items-center p-4">
                          <div className="w-full">
                            <p className="text-sm font-medium text-gray-900">
                              { title }
                            </p>
                            { renderBody(body) }
                          </div>
                        </div>
                        <div className="flex">
                          <div className="flex flex-col divide-y divide-gray-200 border-l border-gray-200">
                            { markSeen &&
                              <div className="h-0 flex-1 flex">
                                <button onClick={markSeen} className="w-full px-4 py-3 flex items-center justify-center text-sm font-medium text-primary hover:text-primary-lighter focus:outline-none focus:z-10 focus:ring-2 focus:ring-primary-lighter" title={formatMessage(messages.markSeen)}>
                                  <FormattedMessage {...messages.markSeen}/>
                                </button>
                              </div>
                            }
                            { url &&
                              <div className="h-0 flex-1 flex">
                                <Link onClick={markSeen} to={url} className="w-full px-4 py-3 flex items-center justify-center text-sm font-medium text-gray-700 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-primary">
                                  <span className="mr-2"><FormattedMessage{...messages.go}/></span>
                                  <Icon icon="long-arrow-right" />
                                </Link>
                              </div>
                            }
                          </div>
                        </div>
                      </li>
                    )) }

                    <li className="pointer-events-auto flex">
                      <Link
                        to="?showNotifications=hell-yes"
                        className="block w-full px-4 py-2 text-sm text-primary hover:bg-gray-100"
                        role="menuitem"
                      >
                        Show all notifications
                      </Link>
                    </li>
                  </ul>
                </Transition>
              </div>



              <div className="ml-3 relative">
                <div>
                  <button
                    onBlur={() => setTimeout(() => setShowUserMenu(false), 200)}
                    onClick={() => {
                      setShowUserMenu(!showUserMenu)
                      track.increment('navigation.cta', { action: 'toggleUserMenu' })
                    }}
                    className="w-8 max-w-xs bg-white flex items-center text-sm rounded-full focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-darker"
                    id="user-menu"
                    aria-haspopup="true"
                  >
                    <span className="sr-only"><FormattedMessage{...messages.openUserMenu}/></span>
                    <img className="block h-8 w-8 rounded-full" src={image} alt={name} />
                  </button>
                </div>

                <Transition
                  show={showUserMenu}
                  enter="transition ease-out duration-100"
                  enterFrom="transform opacity-0 scale-95"
                  enterTo="transform opacity-100 scale-100"
                  leave="transition ease-in duration-75"
                  leaveFrom="transform opacity-100 scale-100"
                  leaveTo="transform opacity-0 scale-95"
                  className="origin-top-right absolute right-0 mt-2 w-48 rounded-sm shadow-md bg-white ring-1 ring-black ring-opacity-5 divide-y divide-gray-100"
                  role="menu"
                  aria-orientation="vertical"
                  aria-labelledby="user-menu"
                >
                  <div className="px-4 py-3">
                    <p className="text-sm">
                      <FormattedMessage {...messages.signedInAs}/>
                    </p>
                    <p className="text-sm font-medium text-gray-900 truncate">
                      { username }
                    </p>
                  </div>

                  <div>
                    {[
                      ['/settings', 'yourProfile'],
                      ['/settings/notifications', 'notificationSettings'],
                      ['/settings/security', 'securitySettings'],
                    ].map(([to, message]) => (
                      <Link
                        key={to}
                        to={to}
                        className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                        role="menuitem"
                        onClick={() => track.increment('navigation', { path: to })}
                      >
                        <FormattedMessage{...messages[message]} />
                      </Link>
                    )) }
                  </div>

                  <a
                    href="/logout"
                    className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    role="menuitem"
                    onClick={() => track.increment('navigation', { action: 'logout' })}
                  >
                    <FormattedMessage{...messages.signOut} />
                  </a>
                </Transition>
              </div>
            </div>
          </div>
        </div>
        <main className="flex-1 relative overflow-y-auto focus:outline-none" tabIndex="0">
          { children }
        </main>
      </div>
    </div>
  )
}

export default withRouter(SidebarLayout)
